// /app/frontend/src/pages/seo/PolyFamilyPlanningPage.jsx
// Focus: PRACTICAL CHECKLISTS — kids, finances, household, legal, long-term vision.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

const CHECKLIST_SECTIONS = [
  {
    title: 'Vision & Values',
    items: [
      'Have you written down what your family is for — the why behind it?',
      'Do all current and future partners agree on the long-term destination?',
      'Have you defined the kind of community and culture your family wants to model?',
      'Is there shared language for talking about the family with outsiders?',
    ],
  },
  {
    title: 'Children & Parenting',
    items: [
      'Has every adult discussed whether they want children, when, and how many?',
      'Have you aligned on parenting philosophy — discipline, education, screen time, faith?',
      'Is each adult\u2019s role in each child\u2019s life clearly named?',
      'Have you discussed how the family structure will be explained to children?',
      'Is there a plan for how new children will be welcomed by existing children?',
    ],
  },
  {
    title: 'Finances',
    items: [
      'Is there transparency about every adult\u2019s income, debts, and assets?',
      'Have you agreed on shared vs. individual expenses?',
      'Is there a joint household budget — and who manages it?',
      'Have you discussed savings, retirement, and emergency funds for everyone?',
      'Is there a plan for what happens financially if someone leaves the family?',
    ],
  },
  {
    title: 'Household & Logistics',
    items: [
      'One household, multiple households, or hybrid — and is everyone clear on the model?',
      'Whose name is on the lease, mortgage, or deed? Is that intentional?',
      'How is domestic labor divided? Who cooks, who cleans, who handles repairs?',
      'How is time managed — equal nights, anchor days, scheduled couple time?',
      'Are there shared family meals or rituals that anchor the week?',
    ],
  },
  {
    title: 'Legal Realities',
    items: [
      'Only one marriage is legally recognized in most jurisdictions. Have you discussed this honestly?',
      'Do you have wills that name every partner appropriately?',
      'Have you discussed powers of attorney and medical decision-making?',
      'Are insurance beneficiaries set the way the family actually wants them?',
      'Have you spoken with an attorney familiar with non-traditional families?',
    ],
  },
  {
    title: 'Health & Wellbeing',
    items: [
      'Is there a sexual health protocol — testing, communication, agreements?',
      'Does every adult have access to a therapist or counselor if needed?',
      'Are there regular family check-ins — weekly, monthly, or quarterly?',
      'Is there space for individual hobbies, friendships, and growth outside the family?',
    ],
  },
];

export default function PolyFamilyPlanningPage() {
  const path = '/poly-family-planning';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Poly Family Planning', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Poly Family Planning Guide"
        description="Explore family planning, parenting, communication, and long-term vision within poly and plural family structures."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Planning For A Growing Family"
        sub="The practical checklist most plural families wish they had built earlier. Not legal or financial advice — but the conversations that protect every adult and child involved."
        body="Print this. Walk through it together. Revisit yearly."
        primaryCta={{ label: 'Create Your Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Building A Polygamous Family', to: '/building-a-polygamous-family' }}
      />

      <main className="max-w-3xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-10">
          <p className="text-lg text-slate-700 leading-relaxed">
            Plural family-building shines a bright light on every assumption a couple ever made about how a family runs. The list below covers the conversations the most stable families say they wish they had had earlier — about kids, money, household, law, and the long arc of life together. It is not exhaustive, and it is not a substitute for working with professionals on the legal and financial pieces. It is a starting list.
          </p>
        </section>

        {/* Checklist sections */}
        {CHECKLIST_SECTIONS.map((section) => (
          <section key={section.title} className="mb-10">
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              {section.title}
            </h2>
            <ul className="space-y-2.5">
              {section.items.map((q) => (
                <li
                  key={q}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                >
                  <span
                    aria-hidden
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-slate-300"
                  />
                  <span className="text-slate-800">{q}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* Honest disclaimer */}
        <section className="mb-12 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-amber-900 mb-2">
            An Honest Note
          </h2>
          <p className="text-amber-900/90 text-sm leading-relaxed">
            This checklist is for conversation, not for legal or financial decision-making. The legal landscape for plural families varies dramatically by country and state. Before signing anything — wills, leases, custody agreements — work with an attorney who has direct experience with non-traditional families.
          </p>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/sister-wives-and-poly-family-building" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Sister Wives & Poly Family Building</p>
              <p className="text-sm text-slate-600 mt-1">Dynamics inside a plural family.</p>
            </a>
            <a href="/building-a-polygamous-family" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Building A Polygamous Family</p>
              <p className="text-sm text-slate-600 mt-1">Values and foundation.</p>
            </a>
            <a href="/safe-poly-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Safe Poly Dating Guide</p>
              <p className="text-sm text-slate-600 mt-1">Practical safety for poly relationships.</p>
            </a>
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">Foundations of consent and care.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
